import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { fontFamily } from "../Constants/constants";
import { getFileSasUrl, getFileBytes } from "../../API_Endpoints/APIEndpoints";
import UniversalDocumentViewer from "./UniversalDocumentViewer";

// Module-level cache: keyed by "fileImportId|fileName" to avoid re-fetching the same file
const fileCache = new Map();

/**
 * SecureDocumentViewer - A component that fetches file bytes from the backend
 * and displays them using UniversalDocumentViewer
 */
function SecureDocumentViewer({
  organizationId,
  userId,
  fileImportId,
  fileName,
  style,
  fallbackUrl,
  onError,
  onLoad,
  fileType,
  businessunit,
  filePath,
  useFastLoad = false,
}) {
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFileData = async () => {
      try {
        setLoading(true);
        setError(null);
        const extension = fileName ? fileName.split(".").pop()?.toLowerCase() : "";
        const type = fileType || extension || "pdf";

        const cacheKey = `${fileImportId}|${fileName}`;
        if (fileCache.has(cacheKey)) {
          setFileData(fileCache.get(cacheKey));
          if (onLoad) onLoad(fileCache.get(cacheKey));
          return;
        }

        // Try SAS URL first — browser streams directly from Azure, no proxy needed
        try {
          if (useFastLoad) {
            const sasUrl = await getFileSasUrl(
              organizationId,
              userId,
              fileImportId,
              fileName,
              extension,
              type,
              businessunit,
              filePath
            );
            if (sasUrl) {
              const data = { sasUrl, filename: fileName, extension };
              fileCache.set(cacheKey, data);
              setFileData(data);
              if (onLoad) onLoad(data);
              return;
            }
          }
        } catch {
          // SAS URL unavailable — fall through to byte download
        }

        // Fallback: download full bytes through backend
        const data = await getFileBytes(
          organizationId,
          userId,
          fileImportId,
          fileName,
          extension,
          type,
          businessunit,
          filePath
        );

        fileCache.set(cacheKey, data);
        setFileData(data);

        // Call onLoad callback if provided
        if (onLoad) {
          onLoad(data);
        }
      } catch (err) {
        console.error("❌ Error fetching file bytes:", err);
        setError(err.message);

        // Call onError callback if provided
        if (onError) {
          onError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have required parameters
    if (organizationId && userId && fileImportId && fileName) {
      fetchFileData();
    } else {
      setLoading(false);
      setError("Missing required parameters: organizationId, userId, fileImportId, or fileName");
    }
  }, [organizationId, userId, fileImportId, fileName, businessunit, filePath]);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          flexDirection: "column",
          gap: "10px",
          ...style,
        }}
      >
        <CircularProgress style={{ color: "var(--loader-color)" }} />
        <div
          style={{
            fontFamily,
            fontSize: "14px",
            color: "#666",
          }}
        >
          Loading document...
        </div>
      </div>
    );
  }

  // Error state - fallback to URL if available
  if (error) {
    if (fallbackUrl) {
      console.log("🔄 Using fallback URL due to error:", fallbackUrl);
      return <UniversalDocumentViewer fileName={fileName} fileUrl={fallbackUrl} style={style} />;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          flexDirection: "column",
          gap: "10px",
          fontFamily,
          color: "#d32f2f",
          ...style,
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "bold" }}>Unable to load document</div>
        <div style={{ fontSize: "12px", textAlign: "center", maxWidth: "300px" }}>{error}</div>
      </div>
    );
  }

  // Success state - display the document
  if (fileData) {
    return (
      <UniversalDocumentViewer
        fileBytes={fileData.fileBytes}
        fileUrl={fileData.sasUrl}
        mimeType={fileData.mimeType}
        fileName={fileData.filename}
        style={style}
      />
    );
  }

  // Default fallback
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "400px",
        fontFamily,
        color: "#666",
        ...style,
      }}
    >
      No document to display
    </div>
  );
}

export default SecureDocumentViewer;
